import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';

/**
 * Global Date Interceptor
 * - Converts outgoing 'DD-MM-YYYY' strings to 'YYYY-MM-DD' (ISO) for the backend.
 * - Converts incoming 'YYYY-MM-DD' strings back to 'DD-MM-YYYY' for the UI.
 */
export const dateInterceptor: HttpInterceptorFn = (req, next) => {
  // 1. Handle Outgoing Request (Format for Backend)
  let clonedParams = req.params;
  req.params.keys().forEach(key => {
    const value = req.params.get(key);
    if (value && /^\d{1,2}-\d{1,2}-\d{4}/.test(value)) {
      const [d, m, y] = value.split('-');
      clonedParams = clonedParams.set(key, `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
    }
  });

  // Binary/multipart bodies must pass through untouched: spreading a FormData
  // or Blob with {...obj} yields an empty object, silently discarding the file
  // and every form field (JPIP2-2578 sign-off-upload 400s).
  let clonedBody = req.body;
  if (req.body && typeof req.body === 'object' && !isBinaryBody(req.body)) {
    clonedBody = convertObjectDates(req.body, 'TO_ISO');
  }

  const modifiedReq = req.clone({
    params: clonedParams,
    body: clonedBody
  });

  // 2. Handle Incoming Response (Format for UI)
  return next(modifiedReq).pipe(
    map(event => {
      if (event instanceof HttpResponse && event.body && !isBinaryBody(event.body)) {
        const transformedBody = convertObjectDates(event.body, 'TO_BRITISH');
        return event.clone({ body: transformedBody });
      }
      return event;
    })
  );
};

/**
 * Bodies that must never be cloned via object spread — doing so destroys them.
 */
function isBinaryBody(body: unknown): boolean {
  return (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams
  );
}

/**
 * Recursive helper to find and transform date strings within objects/arrays.
 */
function convertObjectDates(obj: any, direction: 'TO_ISO' | 'TO_BRITISH'): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  if (isBinaryBody(obj)) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertObjectDates(item, direction));
  }

  if (obj instanceof Date) {
    if (direction === 'TO_ISO') {
      if (isNaN(obj.getTime())) {
        return null;
      }
      const y = obj.getFullYear();
      const m = String(obj.getMonth() + 1).padStart(2, '0');
      const d = String(obj.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return obj;
  }

  const newObj = { ...obj };
  for (const key of Object.keys(newObj)) {
    const value = newObj[key];

    if (typeof value === 'string') {
      // TO_ISO: DD-MM-YYYY -> YYYY-MM-DD
      if (direction === 'TO_ISO' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value)) {
        const [d, m, y] = value.split('-');
        newObj[key] = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      // TO_BRITISH: YYYY-MM-DD -> DD-MM-YYYY
      else if (direction === 'TO_BRITISH' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [y, m, d] = value.split('-');
        newObj[key] = `${d}-${m}-${y}`;
      }
    } else if (typeof value === 'object') {
      newObj[key] = convertObjectDates(value, direction);
    }
  }
  return newObj;
}
